import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

import {
  DEFAULT_TOOLTIP_PADDING,
  placeAnchoredTooltip,
  viewportTooltipBounds,
  type PlaceAnchoredTooltipResult,
  type RectLike,
} from "@/lib/placeAnchoredTooltip";
import { cn } from "@/lib/utils";

export interface AnchoredTooltipProps {
  children: ReactNode;
  /** Element the caret should point at. Re-read on scroll/resize. */
  anchorRef?: RefObject<HTMLElement | null>;
  /** Point or rect fallback when there is no element (chart cursor). */
  anchor?: RectLike;
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Keep false for cursor-following chart tips. */
  pointerEvents?: boolean;
  /** Soft cap before the viewport clamp. */
  maxWidth?: number;
  /** 300ms fade/slide on first show; off for cursor-following chart tips. */
  animateIn?: boolean;
}

function readAnchor(
  anchorRef: RefObject<HTMLElement | null> | undefined,
  anchor: RectLike | undefined,
): RectLike {
  const el = anchorRef?.current;
  if (el) {
    const r = el.getBoundingClientRect();
    return { x: r.left, y: r.top, width: r.width, height: r.height };
  }
  return anchor ?? { x: 0, y: 0, width: 0, height: 0 };
}

/**
 * Portaled tooltip bubble with a caret aimed at its trigger. Clamped to the
 * viewport so long copy wraps and the box never paints off-screen.
 */
export function AnchoredTooltip({
  children,
  anchorRef,
  anchor,
  className,
  onMouseEnter,
  onMouseLeave,
  pointerEvents = true,
  maxWidth = 320,
  animateIn = true,
}: AnchoredTooltipProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const updateRef = useRef<() => void>(() => {});
  const [place, setPlace] = useState<PlaceAnchoredTooltipResult | null>(null);
  const [bounds, setBounds] = useState(() =>
    typeof window === "undefined"
      ? { maxWidth: 320, maxHeight: 240 }
      : viewportTooltipBounds({
          width: window.innerWidth,
          height: window.innerHeight,
        }),
  );

  updateRef.current = () => {
    const el = boxRef.current;
    if (!el) return;
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
    const nextBounds = viewportTooltipBounds(viewport);
    setBounds(nextBounds);
    const box = el.getBoundingClientRect();
    setPlace(
      placeAnchoredTooltip({
        anchor: readAnchor(anchorRef, anchor),
        tooltip: { width: box.width, height: box.height },
        viewport,
      }),
    );
  };

  useLayoutEffect(() => {
    const onWin = () => updateRef.current();
    onWin();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [anchor, anchorRef, children]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      role="tooltip"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        "anchored-tooltip z-[9999]",
        place && "anchored-tooltip--placed",
        place?.side === "top" && "anchored-tooltip--top",
        place?.side === "bottom" && "anchored-tooltip--bottom",
        animateIn && "anchored-tooltip--animate-in",
        pointerEvents ? "pointer-events-auto" : "pointer-events-none",
      )}
      style={{
        position: "fixed",
        top: place?.top ?? 0,
        left: place?.left ?? 0,
        width: "max-content",
        maxWidth: Math.min(maxWidth, bounds.maxWidth),
      }}
    >
      <div
        ref={boxRef}
        className={cn(
          "glass-overlay relative rounded-md px-2 py-1 text-xs leading-snug text-popover-foreground",
          className,
        )}
        style={{
          maxWidth: Math.min(maxWidth, bounds.maxWidth),
          maxHeight: bounds.maxHeight,
        }}
      >
        <div
          className="overflow-y-auto break-words [overflow-wrap:anywhere]"
          style={{
            maxHeight: Math.max(0, bounds.maxHeight - DEFAULT_TOOLTIP_PADDING),
          }}
        >
          {children}
        </div>
      </div>
      {place && (
        <span
          aria-hidden
          className={cn(
            "tooltip-caret",
            place.side === "top" ? "tooltip-caret--top" : "tooltip-caret--bottom",
          )}
          style={{ left: place.arrowLeft }}
        />
      )}
    </div>,
    document.body,
  );
}
